import React, {useState, forwardRef} from 'react';

import {Input} from '../input';


import type {ComponentType, Ref, ComponentProps} from 'react';

import type {InputProps} from '../input';

type NativeInputProps = Omit<
    ComponentProps<'input'>,
    'ref' | 'value' | 'onFocus' | 'onBlur'
>;

export interface CustomInputProps extends NativeInputProps {
    onFocus?: InputProps['onFocus'],
    onBlur?: InputProps['onBlur'],
    ref?: Ref<HTMLInputElement>,
    value?: InputProps['value'],
}

type OmittedInputProps = Omit<InputProps, 'inputComponent'>;

export interface SuffixedInputProps extends OmittedInputProps {
    /**
     * Suffix to be shown in value when input is not focused.
     * */
    suffix: string,
    /**
     * Custom input component.
     * */
    inputComponent?: ComponentType<CustomInputProps>,
    /**
     * Ref to input element.
     * */
    ref?: Ref<HTMLInputElement>,
}

/*
* @description
* Component for input with suffix in value.
*
* ## Features
* Shows suffix in value when input is not focused.
*
* ### Accessibility
It accepts all native input attributes that are available in HTML. Please follow standard requirement for accessibility, e.g. https://www.w3.org/WAI/tutorials/forms/instructions/.
- Use `aria-label` or `aria-labelledby` to provide a label for the input.
- Use `aria-describedby` to provide a description for the input.
*
* @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-input-input-suffix--documentation}
* */
export const SuffixedInput = forwardRef<HTMLInputElement, SuffixedInputProps>(function InputSuffix(props, ref) {
    const {
        suffix: suffixProp,
        inputComponent: InputComponent = Input,
        ...nativeProps
    } = props;
    const [isActive, setActive] = useState<boolean>();

    const suffix = (nativeProps.value || nativeProps.value === 0) && !isActive ? suffixProp : '';

    return (
        <InputComponent
            {...nativeProps}
            onFocus={e => {
                setActive(true);
                nativeProps.onFocus?.(e);
            }}
            onBlur={e => {
                setActive(false);
                nativeProps.onBlur?.(e);
            }}
            value={suffix ? nativeProps.value + suffix : nativeProps.value}
            ref={ref}
        />
    );
});

SuffixedInput.displayName = 'SuffixedInput';
